import { Component, Input } from '@angular/core';
import { EMAIL_PLACEHOLDERS } from './email-placeholders';

/** A field a placeholder can go into: a text input / textarea, or an email body editor. */
export interface PlaceholderTarget {
    label: string;
    input?: HTMLInputElement | HTMLTextAreaElement;   // inserted at the cursor
    editor?: boolean;                                   // the dialog's TinyMCE body: at its cursor
    // Writes the new value back to the model (inputs; the editor updates its own model).
    set?: (value: string) => void;
}

/**
 * Mockup .email-placeholder-tools: "Insert into" a field, pick a "Dynamic placeholder", Insert.
 * Used by the Appointment Emails and Canned Replies editors.
 */
@Component({
    selector: 'app-placeholder-insert',
    template: `
        <div class="pi-tools">
            <label class="sr-field">
                <span>Insert into</span>
                <select class="sr-control" [(ngModel)]="target" [ngModelOptions]="{standalone: true}" aria-label="Placeholder destination">
                    <option *ngFor="let t of targets; let i = index" [ngValue]="i">{{t.label}}</option>
                </select>
            </label>
            <label class="sr-field">
                <span>Dynamic placeholder</span>
                <select class="sr-control" [(ngModel)]="token" [ngModelOptions]="{standalone: true}" aria-label="Dynamic placeholder">
                    <option *ngFor="let p of placeholders" [ngValue]="p[0]">{{p[1]}} — {{p[0]}}</option>
                </select>
            </label>
            <button class="sr-btn" type="button" (click)="insert()">Insert</button>
        </div>
    `,
    // The shared settings controls (.sr-field, .sr-control, .sr-btn) are scoped per component.
    styleUrls: ['./settings-records.scss'],
    styles: [`
        .pi-tools { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr) auto; align-items: end; gap: 12px;
            padding: 12px; border: 1px dashed var(--im-line-strong, #b9c8d9); border-radius: 8px; background: var(--im-soft); }
        .pi-tools .sr-field { margin: 0; }
        @media (max-width: 640px) { .pi-tools { grid-template-columns: 1fr; } }
    `]
})
export class PlaceholderInsertComponent {
    @Input() targets: PlaceholderTarget[] = [];
    readonly placeholders = EMAIL_PLACEHOLDERS;
    target = 0;
    token = EMAIL_PLACEHOLDERS[0][0];

    insert() {
        const target = this.targets[this.target];
        if (!target) {
            return;
        }
        if (target.editor) {
            const tinymce = (window as any).tinymce;
            const editor = tinymce && tinymce.activeEditor;
            if (editor) {
                editor.focus();
                editor.insertContent(this.token);
            }
            return;
        }
        const field = target.input;
        if (!field) {
            return;
        }
        const start = field.selectionStart === null ? field.value.length : field.selectionStart;
        const end = field.selectionEnd === null ? start : field.selectionEnd;
        if (field.maxLength > 0 && field.value.length - (end - start) + this.token.length > field.maxLength) {
            return;
        }
        const value = field.value.slice(0, start) + this.token + field.value.slice(end);
        field.value = value;
        if (target.set) {
            target.set(value);
        }
        field.focus();
        field.setSelectionRange(start + this.token.length, start + this.token.length);
    }
}
